/**
 * Demo-only descriptions for the MCP catalog tools. No API returns a tool
 * description yet, so these exist only in the mock stores and the Resources
 * column that shows them is hidden whenever a service's tools carry none.
 *
 * Keyed by `name|dataAccess`: several servers expose a tool with the same name
 * (`run_query`, `search_articles`) over different data.
 */
const DESCRIPTIONS: Record<string, string> = {
  // SharePoint - client onboarding & KYC
  "findFileOrFolder|Documents": "Find files and folders by name or path",
  "readSmallTextFile|Documents": "Read the contents of a text document",
  "readSmallBinaryFile|Documents": "Read a binary document such as a PDF",
  "getSensitivityLabels|Compliance metadata": "List sensitivity labels applied to files",
  "setSensitivityLabelOnFile|Compliance metadata": "Apply or change a file's sensitivity label",
  "shareFileOrFolder|Documents": "Share a file or folder with other people",
  "redactClientPii|Client documents": "Redact client personal data in documents",
  // SharePoint lists
  "listLists|Lists": "List the SharePoint lists on a site",
  "listListItems|Lists": "List the items in a SharePoint list",
  "getListItem|Lists": "Get one list item and its fields",
  "createListItem|Lists": "Add an item to a SharePoint list",
  "updateListItem|Lists": "Update the fields of a list item",
  "flagRetentionException|Records retention": "Flag a record as a retention exception",
  // Snowflake - trading
  "run_query|Trading & P&L tables": "Run SQL against trading and P&L tables",
  "describe_table|Trading & P&L tables": "Describe a table's columns and types",
  "get_query_history|Query metadata": "Review recently executed queries",
  "get_table_sample|Trading & P&L tables": "Preview sample rows from a table",
  "getTradeSurveillanceAlerts|Trade surveillance system": "Retrieve trade surveillance alerts",
  // Snowflake - risk
  "run_query|Risk & exposure tables": "Run SQL against risk and exposure tables",
  "list_views|Risk models": "List the views behind risk models",
  "get_lineage|Data lineage": "Trace upstream and downstream data lineage",
  "mask_column|Sensitive risk fields": "Apply masking to a sensitive column",
  "computeVarExposure|Value-at-risk engine": "Compute value-at-risk exposure",
  // Snowflake - governed warehouse
  "run_query|Governed warehouse": "Run SQL against the governed warehouse",
  "list_databases|Warehouse catalog": "List databases in the warehouse catalog",
  "list_warehouses|Compute resources": "List available compute warehouses",
  "grant_privilege|Access grants": "Grant a privilege on a warehouse object",
  "revoke_privilege|Access grants": "Revoke a privilege on a warehouse object",
  // Databricks
  "list_clusters|Compute clusters": "List compute clusters and their state",
  "run_notebook|Trading systems notebooks": "Run a trading systems notebook",
  "create_job|Job definitions": "Create a scheduled job definition",
  "get_job_run|Job run history": "Get the status and output of a job run",
  "deployTradingAlgoCanary|Trading algorithm deployment": "Deploy a trading algorithm canary",
  "query_table|Lakehouse risk tables": "Query lakehouse risk tables",
  "run_job|Model batch jobs": "Run a model batch job",
  "list_experiments|Model experiments": "List model experiments and runs",
  "register_model|Model registry": "Register a model version",
  "backtestPricingModel|Pricing model backtests": "Backtest a pricing model",
  // GitHub
  "get_file_contents|Source code": "Read a file from a repository",
  "list_branches|Source code": "List branches in a repository",
  "list_issues|Issue tracker": "List issues in the issue tracker",
  "create_pull_request|Source code": "Open a pull request",
  "delete_branch|Repository administration": "Delete a repository branch",
  // Knowledge bases
  "search_articles|HR handbook": "Search HR handbook articles",
  "get_article|HR handbook": "Get an HR handbook article",
  "list_categories|HR handbook": "List HR handbook categories",
  "search_articles|Service knowledge base": "Search service knowledge base articles",
  "get_case_history|Client case history": "Get a client's case history",
  "escalate_case|Case management": "Escalate a case to the next tier",
  "summarizeClientInteraction|Client interaction transcripts": "Summarize a client interaction transcript",
  "search_articles|Research library": "Search research library publications",
  "list_recent_publications|Research library": "List recent research publications",
  "flagMarketSensitiveContent|Market-sensitive research": "Flag market-sensitive research content",
  // Microsoft 365
  "search_mail|Mail and calendar": "Search mail and calendar items",
  "share_document|Workspace content": "Share a workspace document",
  "draftClientCorrespondence|Client correspondence": "Draft client correspondence",
  // Investment banking demo - SharePoint support
  "searchSites|Support knowledge": "Search support SharePoint sites",
  "searchDocuments|Support knowledge": "Search support documents",
  "getDocument|Support knowledge": "Get a support document",
  "listLibraries|Support knowledge": "List document libraries",
  "listFolderContents|Support knowledge": "List the contents of a folder",
  "readTextFile|Support knowledge": "Read a text file",
  "getFileMetadata|Support knowledge": "Get a file's metadata",
  "getVersionHistory|Support knowledge": "Get a file's version history",
  "getSensitivityLabel|Support knowledge": "Get a file's sensitivity label",
  "listCaseAttachments|Case attachments": "List attachments on a case",
  "uploadCaseAttachment|Case attachments": "Upload an attachment to a case",
  "shareDocumentLink|Support knowledge": "Share a link to a document",
  // Investment banking demo - ServiceNow
  "searchIncidents|Service tickets": "Search customer incidents and tickets",
  "getIncident|Service tickets": "Get incident details",
  "listIncidentComments|Service tickets": "List comments for an incident",
  "searchKnowledgeBase|Support knowledge": "Search knowledge base articles",
  "getCaseHistory|Case history": "Get case history and activities",
  "getAssignmentGroup|Service tickets": "Get assignment group details",
  "updateIncident|Service tickets": "Update incident details",
  "addWorkNote|Service tickets": "Add a work note to an incident",
  // Investment banking demo - Salesforce
  "searchAccounts|Customer accounts": "Search customer accounts",
  "getAccount|Customer accounts": "Get customer account details",
  "getContact|Customer contacts": "Get customer contact details",
  "searchCases|Customer cases": "Search customer cases",
  "getCase|Customer cases": "Get customer case details",
  "listCaseComments|Customer cases": "List comments on a case",
  "getEntitlements|Customer accounts": "Get a customer's entitlements",
  "logInteraction|Customer cases": "Log a customer interaction",
};

export const toolDescription = (
  name: string,
  dataAccess: string,
): string | undefined => DESCRIPTIONS[`${name}|${dataAccess}`];
